import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";

// Employee count + status counts (Active / OnLeave / Recent)
export const useEmployeeStats = ({ profile, onCountsUpdate, onEmployeeCountUpdate, setError }) => {
  // State for employee count
  const [employeeCount, setEmployeeCount] = useState(0);
  // State for different employee status counts
  const [counts, setCounts] = useState({ Active: 0, OnLeave: 0, Recent: 0 });

  // Function to fetch total employee count (filtered by client name)
  const fetchEmployeeCount = async () => {
    try {
      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      // Call the grouped API
      console.log("📡 Fetching employee counts from all clients");
      const response = await apiFetch(`${API_URL}/employees/clients/count`, { method: "GET" });
      const data = await response.json(); // Array of { name, code, count }

      console.log(`✅ Received count data for ${data.length} clients`);

      let totalCount;

      // FIXED: Proper conditional logic for client filtering
      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter data for specific client only
        console.log(`🔍 Filtering count for client: ${clientName}`);
        const filteredData = data.filter(
          (client) => client.name?.toLowerCase() === clientName.toLowerCase()
        );

        // Calculate total count for the selected client
        totalCount = filteredData.reduce(
          (sum, client) => sum + client.count,
          0
        );
        console.log(`✅ Total count for ${clientName}: ${totalCount}`);
      } else {
        // Calculate total count for all clients (Infinity or no client set)
        console.log("🔍 Calculating total count for all clients");
        totalCount = data.reduce((sum, client) => sum + client.count, 0);
        console.log(`✅ Total count for all clients: ${totalCount}`);
      }

      // Update total employee count
      setEmployeeCount(totalCount);
      if (typeof onEmployeeCountUpdate === "function") {
        onEmployeeCountUpdate(totalCount);
      }
      console.log("✅ Employee count updated successfully!");
    } catch (err) {
      console.error("❌ Error fetching employee count:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setError("Failed to fetch employee count");
    }
  };

  const fetchEmployeeCounts = async () => {
    try {
      console.log("🔍 Starting fetchEmployeeCounts...");
      console.log("📍 API_URL:", API_URL);

      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      console.log(`📍 Client Name: ${clientName}`);

      // Fetch active employees by client
      console.log(
        "📡 Fetching Active counts from:",
        `${API_URL}/employees/clients/Active/count`
      );
      const activeRes = await apiFetch(
        `${API_URL}/employees/clients/Active/count`,
        { method: "GET" }
      );
      console.log("✅ Active Response Status:", activeRes.status);
      console.log(
        "✅ Active Response Data:",
        JSON.stringify(activeRes.data, null, 2)
      );

      // Fetch on-leave employees by client
      console.log(
        "📡 Fetching OnLeave counts from:",
        `${API_URL}/employees/clients/OnLeave/count`
      );
      const onLeaveRes = await apiFetch(
        `${API_URL}/employees/clients/OnLeave/count`,
        { method: "GET" }
      );
      console.log("✅ OnLeave Response Status:", onLeaveRes.status);
      console.log(
        "✅ OnLeave Response Data:",
        JSON.stringify(onLeaveRes.data, null, 2)
      );

      const activeData = await activeRes.json();
      const onLeaveData = await onLeaveRes.json();

      // Verify data is array
      console.log(
        "🔍 Active data type:",
        Array.isArray(activeData) ? "Array" : typeof activeData
      );
      console.log(
        "🔍 OnLeave data type:",
        Array.isArray(onLeaveData) ? "Array" : typeof onLeaveData
      );

      if (!Array.isArray(activeData)) {
        console.error("❌ Active data is NOT an array:", activeData);
        throw new Error("Active data is not an array");
      }
      if (!Array.isArray(onLeaveData)) {
        console.error("❌ OnLeave data is NOT an array:", onLeaveData);
        throw new Error("OnLeave data is not an array");
      }

      console.log("📊 Active data length:", activeData.length);
      console.log("📊 OnLeave data length:", onLeaveData.length);

      let clientActiveItems;
      let clientOnLeaveItems;

      // FIXED: Proper conditional logic for client filtering
      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter for specific client
        console.log(`🔍 Filtering counts for client: ${clientName}`);

        clientActiveItems = activeData.filter((item) => {
          const itemName = item?.name?.toLowerCase();
          const match = itemName === clientName.toLowerCase();
          console.log(
            `🔍 Checking active item - Name: "${item?.name}", Lowercase: "${itemName}", Match: ${match}`
          );
          return match;
        });

        clientOnLeaveItems = onLeaveData.filter((item) => {
          const itemName = item?.name?.toLowerCase();
          const match = itemName === clientName.toLowerCase();
          console.log(
            `🔍 Checking onLeave item - Name: "${item?.name}", Lowercase: "${itemName}", Match: ${match}`
          );
          return match;
        });
      } else {
        // Use all data for Infinity or when no client is set
        console.log("🔍 Using counts for all clients (Infinity)");
        clientActiveItems = activeData;
        clientOnLeaveItems = onLeaveData;
      }

      console.log(
        "✅ Filtered Active Items:",
        JSON.stringify(clientActiveItems, null, 2)
      );
      console.log(
        "✅ Filtered OnLeave Items:",
        JSON.stringify(clientOnLeaveItems, null, 2)
      );

      // Calculate totals
      const activeCount = clientActiveItems.reduce((sum, item) => {
        console.log(
          `➕ Adding active count: ${item.count} (current sum: ${sum})`
        );
        return sum + (item.count || 0);
      }, 0);

      const onLeaveCount = clientOnLeaveItems.reduce((sum, item) => {
        console.log(
          `➕ Adding onLeave count: ${item.count} (current sum: ${sum})`
        );
        return sum + (item.count || 0);
      }, 0);

      console.log(`📈 FINAL COUNTS:`);
      console.log(`   - Active: ${activeCount}`);
      console.log(`   - OnLeave: ${onLeaveCount}`);
      console.log(`   - Total: ${activeCount + onLeaveCount}`);

      // Update state
      const updatedCounts = {
        Active: activeCount,
        OnLeave: onLeaveCount,
        Recent: 0,
        Total: activeCount + onLeaveCount,
      };

      setCounts(updatedCounts);
      if (typeof onCountsUpdate === "function") {
        onCountsUpdate(updatedCounts);
      }

      console.log("✅ State updated successfully!");
    } catch (err) {
      console.error("❌ ERROR in fetchEmployeeCounts:");
      console.error("   - Message:", err.message);
      console.error("   - Response Status:", err.response?.status);
      console.error("   - Response Data:", err.response?.data);
      console.error("   - Full Error:", err);
      setError("Failed to fetch employee statistics");
    }
  };

  return { employeeCount, counts, fetchEmployeeCount, fetchEmployeeCounts };
};
